import { Fragment } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { formatDayTime, withCountRu } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { Failed, failureText } from '../../ui/Load'
import { TableSkeleton } from '../../ui/Skeleton'
import { Select } from '../../ui/Select'
import { PageActions } from '../../app/PageHeader'
import { useSession } from '../auth/session'
import { useExchangeLog, useIntegrations, useSetProvider, type Integration } from './api'
import {
  HEALTH_TEXT,
  groupByCategory,
  health,
  lastSync,
  logoLetter,
  providerText,
  type Health,
} from './model'

export interface IntegrationsScreenProps {
  onToast: (message: string) => void
}

/**
 * Тон состояния: зелёный — работает по-настоящему, заглушка — «пришло, но
 * не всерьёз» (info), выключенная — нейтральная: ничего не горит.
 */
const HEALTH_TONE: Record<Health, string | undefined> = {
  ok: 'success',
  stub: 'info',
  off: undefined,
}

/** Щелчок по списку провайдеров не должен открывать интеграцию. */
const stay = (event: { stopPropagation: () => void }) => {
  event.stopPropagation()
}

/**
 * Внешние системы сети: платежи, логистика, карты и уведомления. У каждой —
 * провайдер, на котором она работает. Ключей API не отдаёт вовсе: они живут в
 * окружении сервера, поэтому здесь переключатель, а не поля с секретами.
 */
export function IntegrationsScreen({ onToast }: IntegrationsScreenProps) {
  const { can } = useSession()
  const navigate = useNavigate()
  const integrations = useIntegrations()
  const setProvider = useSetProvider()
  // Последний обмен по каждой интеграции берём из первой страницы общего
  // журнала (10 самых новых): поля «когда обменивались» в ответе нет.
  const log = useExchangeLog('')

  const canUpdate = can('integrations', 'u')

  function switchProvider(integration: Integration, provider: string) {
    // Список закрыли на том же пункте — менять нечего, запрос не нужен.
    if (provider === integration.provider) return
    setProvider.mutate(
      { key: integration.key, provider },
      {
        onSuccess: () => {
          onToast(`${integration.title}: ${providerText(provider)}`)
        },
        onError: (error) => {
          onToast(failureText(error))
        },
      },
    )
  }

  return (
    <>
      <PageActions>
        {integrations.data ? (
          <span
            className="ui-count adm-count"
            aria-label={withCountRu(integrations.data.length, [
              'интеграция',
              'интеграции',
              'интеграций',
            ])}
          >
            {integrations.data.length}
          </span>
        ) : null}
      </PageActions>

      <section className="ui-table-card" aria-label="Интеграции">
        {integrations.isPending ? <TableSkeleton what="интеграции" /> : null}
        {integrations.isError ? (
          <Failed
            what="интеграции"
            error={integrations.error}
            onRetry={() => {
              void integrations.refetch()
            }}
          />
        ) : null}

        {integrations.data ? (
          <div className="ui-table-wrap">
            <table className="ui-table ui-integrations">
              <colgroup>
                <col />
                <col className="w-provider" />
                <col className="w-state" />
                <col className="w-sync" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">Интеграция</th>
                  <th scope="col">Провайдер</th>
                  <th scope="col">Состояние</th>
                  <th scope="col">Последний обмен</th>
                </tr>
              </thead>
              <tbody>
                {integrations.data.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      <div className="ui-empty">
                        <Icon name="i-plug" />
                        <b>Интеграций нет</b>
                        <p>Сервер не вернул ни одной внешней системы.</p>
                      </div>
                    </td>
                  </tr>
                ) : null}
                {groupByCategory(integrations.data).map(([category, list]) => (
                  <Fragment key={category}>
                    <tr className="group">
                      <th scope="colgroup" colSpan={4}>
                        {category}
                      </th>
                    </tr>
                    {list.map((integration) => {
                      const state = health(integration)
                      const at = lastSync(log.data, integration.key)
                      const href = `/integrations/${integration.key}`
                      return (
                        <tr
                          key={integration.key}
                          data-href={href}
                          onClick={() => {
                            void navigate(href)
                          }}
                        >
                          <td>
                            <div className="ui-product">
                              <span className="ui-avatar" aria-hidden="true">
                                {logoLetter(integration)}
                              </span>
                              <div className="txt">
                                <Link to={href} className="primary" onClick={stay}>
                                  {integration.title}
                                </Link>
                                <span className="secondary">
                                  {integration.note || integration.setting}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td onClick={stay}>
                            {canUpdate ? (
                              <Select
                                size="sm"
                                label={`${integration.title} — провайдер`}
                                value={integration.provider}
                                options={integration.options.map((option) => ({
                                  value: option,
                                  label: providerText(option),
                                }))}
                                onChange={(provider) => {
                                  switchProvider(integration, provider)
                                }}
                              />
                            ) : (
                              providerText(integration.provider)
                            )}
                          </td>
                          <td>
                            <span className="ui-status" data-tone={HEALTH_TONE[state]}>
                              {HEALTH_TEXT[state]}
                            </span>
                          </td>
                          <td className="int-sync">
                            {at === null ? '—' : at ? formatDayTime(at) : 'обменов не было'}
                          </td>
                        </tr>
                      )
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </>
  )
}
